/**
 * FinancialPlanContext — single source of truth for the plan doc + summary.
 *
 * Any child component (`DebtManager`, `Investments`, etc.) can:
 *   const { plan, summary, patch, refresh } = useFinancialPlan();
 *
 * Every patch() call PATCHes the backend and immediately updates local state
 * with the fresh {plan, summary} response — so derived metrics stay in sync.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EV_CURRENCY_CHANGED } from "@/lib/constants";

const FinancialPlanContext = createContext(null);

export function FinancialPlanProvider({ children }) {
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/financial-plan");
      setPlan(data.plan);
      setSummary(data.summary);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load plan");
    } finally { setLoading(false); }
  }, []);

  const patch = useCallback(async (changes) => {
    try {
      const { data } = await api.patch("/financial-plan", changes);
      setPlan(data.plan);
      setSummary(data.summary);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // When the user changes their preferred currency, the plan's currency +
  // every derived summary metric shifts. Refetch so we don't render stale
  // amounts in the previous currency.
  useEffect(() => {
    const onCcy = () => { refresh(); };
    window.addEventListener(EV_CURRENCY_CHANGED, onCcy);
    return () => window.removeEventListener(EV_CURRENCY_CHANGED, onCcy);
  }, [refresh]);

  return (
    <FinancialPlanContext.Provider value={{ plan, summary, loading, error: err, refresh, patch }}>
      {children}
    </FinancialPlanContext.Provider>
  );
}

export function useFinancialPlan() {
  const ctx = useContext(FinancialPlanContext);
  if (!ctx) throw new Error("useFinancialPlan must be used inside FinancialPlanProvider");
  return ctx;
}
